import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { walletModalOpenAtom } from '@/atoms/wallet-modal';
import { type Connector, useConnectors } from '@/hooks/use-connectors';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';

export function WalletConnectorModal() {
  const { t } = useTranslation();
  const { connectors, isConnecting } = useConnectors();
  const [isOpen, setIsOpen] = useAtom(walletModalOpenAtom);

  const kindLabels: Record<Connector['chain'], string> = {
    evm: t('wallet.evm'),
    tron: t('wallet.tron'),
    d9: t('wallet.d9Network'),
  };

  const kindDescriptions: Record<Connector['chain'], string> = {
    evm: t('wallet.evmDescription'),
    tron: t('wallet.tronDescription'),
    d9: t('wallet.d9Description'),
  };

  // Group connectors by kind
  const groupedConnectors = useMemo(() => {
    const groups: Record<Connector['chain'], Connector[]> = {
      evm: [],
      tron: [],
      d9: [],
    };

    for (const connector of connectors) {
      groups[connector.chain].push(connector);
    }

    return groups;
  }, [connectors]);

  const handleConnect = async (connector: Connector) => {
    try {
      if (connector.isConnected) {
        await connector.disconnect();
      } else {
        await connector.connect();
        setIsOpen(false);
      }
    } catch (error) {
      console.error('Failed to connect/disconnect:', error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('wallet.connectYourWallet')}</DialogTitle>
          <DialogDescription>{t('wallet.chooseWallet')}</DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh]">
          <div className="space-y-6 pr-4">
            {isConnecting && (
              <div className="flex items-center justify-center py-4">
                <Spinner className="size-6" />
                <span className="ml-2 text-sm text-muted-foreground">
                  {t('wallet.connecting')}
                </span>
              </div>
            )}

            {!isConnecting &&
              (
                Object.entries(groupedConnectors) as [
                  Connector['chain'],
                  Connector[],
                ][]
              ).map(([kind, kindConnectors]) => {
                if (kind === 'd9' && !kindConnectors.length) return null;
                return (
                  <div key={kind} className="space-y-2">
                    <div>
                      <h3 className="text-sm font-semibold">
                        {kindLabels[kind]}
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        {kindDescriptions[kind]}
                      </p>
                    </div>

                    <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-2">
                      {kindConnectors.map((connector) => (
                        <Button
                          key={connector.id}
                          variant={
                            connector.isConnected ? 'default' : 'outline'
                          }
                          className={cn(
                            'h-auto justify-start gap-3 px-4 py-3 relative',
                            connector.isConnected
                              ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                              : 'hover:bg-accent',
                          )}
                          onClick={() => handleConnect(connector)}
                        >
                          {connector.icon instanceof Object &&
                          'url' in connector.icon ? (
                            <img
                              src={connector.icon.url}
                              alt={connector.name}
                              className="size-6 rounded-full"
                            />
                          ) : (
                            <div className="size-6 rounded-full ring-1 ring-inset ring-foreground/30 bg-muted grid place-items-center">
                              <connector.icon className="size-4" />
                            </div>
                          )}
                          <div className="flex-1 text-left overflow-hidden text-ellipsis">
                            <p className="font-medium leading-5">
                              {connector.name}
                            </p>

                            {connector.isConnected && (
                              <p className="text-xs text-primary-foreground/60 leading-3">
                                {t('wallet.connected')}
                              </p>
                            )}
                          </div>
                          {connector.isConnected && (
                            <div className="size-2 rounded-full bg-green-500 animate-pulse" />
                          )}
                        </Button>
                      ))}
                    </div>

                    {!kindConnectors.length && !isConnecting && (
                      <div className="flex flex-col items-center justify-center py-2 text-center">
                        <p className="text-sm text-muted-foreground">
                          {t('wallet.noConnectorsAvailable')}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {t('wallet.pleaseInstallWallet')}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
